'use client';

import { useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import { ContentBlockFactory } from '@/app/shared/components/content-blocks/ContentBlockFactory';
import { PageErrorBoundary } from '@/app/shared/components/ui/ErrorBoundary';
import { ContentBlockSkeleton } from '@/app/shared/components/ui/ContentBlockSkeleton';
import { LayoutRow, ContentItem } from '@/types/template.types';



// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Normalised record shape used internally by the page.
 * Derived from whatever the API returns (category, news, profile, video, etc.)
 */
interface PageRecord {
  id: number;
  title: string;
  description: string | null;
  /** cat_id or category FK — used as fallback categoryId for block configs */
  cat_id: number | null;
}

interface PageState {
  mainTemplateRows: LayoutRow[];
  leftTemplateRows: LayoutRow[];
  rightTemplateRows: LayoutRow[];
  /** "Detail page additional section" rows, shown after the main template (news/video/profile) */
  additionalSectionRows: LayoutRow[];
  record: PageRecord | null;
  /** Raw API record — passed to item_detail blocks for DetailTplContent rendering */
  rawRecord: Record<string, unknown> | null;
  /** categoryId injected into block configs via processDynamicCategory */
  blockCategoryId: number | null;
  templateType: 'full-width' | 'left-sidebar' | 'right-sidebar' | 'both-sidebar';
  loading: boolean;
  error: string | null;
  retryCount: number;
}

// ─── Prefix → record field map ────────────────────────────────────────────────

/**
 * Given an API response record and its url prefix, extract a normalised PageRecord.
 * Add new prefixes here as the system grows.
 */
function normaliseRecord(prefix: string, raw: Record<string, any>): PageRecord {
  switch (prefix) {
    case 'np':
      return {
        id: raw.id,
        title: raw.news_title,
        description: raw.news_desc ?? null,
        cat_id: raw.cat_id ?? null,
      };
    case 'vp':
      return {
        id: raw.id,
        title: raw.video_name,
        description: raw.video_desc ?? null,
        cat_id: raw.cat_id ?? null,
      };
    case 'pd':
    case 'nr':
      return {
        id: raw.id,
        title: raw.person_name,
        description: null,
        cat_id: raw.category ?? null,
      };
    // ci and ga — the record IS a category row
    case 'ci':
    case 'ga':
      return {
        id: raw.id,
        title: raw.display_name || raw.catname,
        description: raw.catdes ?? null,
        cat_id: raw.id, // category is itself
      };
    // All simple direct-cat_id types (pr, rs, jb, ba, po, vo, sf, ft, fr, ph)
    default:
      return {
        id: raw.id,
        title: raw.display_name || raw.title || raw.name || String(raw.id),
        description: raw.description ?? raw.catdes ?? null,
        cat_id: raw.cat_id ?? null,
      };
  }
}


// ─── Column Tab View ──────────────────────────────────────────────────────────

/**
 * Renders all widgets in a column as a tabbed interface.
 * Tab labels come from the column's `tab_labels` string (comma-separated).
 * If a label is missing for a widget, falls back to the widget's type name.
 */
function ColumnTabView({
  column,
  categoryId,
  rawRecord,
}: {
  column: import('@/types/template.types').LayoutColumn;
  categoryId: string | undefined;
  rawRecord: Record<string, unknown> | null;
}) {
  const labels = (column.tab_labels || '')
    .split(',')
    .map((l: string) => l.trim())
    .filter(Boolean);

  const getLabel = (index: number, item: ContentItem) =>
    labels[index] || item.config?.title || item.type || `Tab ${index + 1}`;

  const [activeTab, setActiveTab] = useState(0);

  if (!column.items || column.items.length === 0) return null;

  return (
    <div className="column-tab-view">
      {/* Tab bar */}
      <div className="flex border-b border-gray-200 mb-4 overflow-x-auto">
        {column.items.map((item: ContentItem, idx: number) => (
          <button
            key={idx}
            onClick={() => setActiveTab(idx)}
            className={[
              'px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors',
              'border-b-2 -mb-px',
              activeTab === idx
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300',
            ].join(' ')}
          >
            {getLabel(idx, item)}
          </button>
        ))}
      </div>

      {/* Active panel */}
      <div>
        {column.items.map((item: ContentItem, idx: number) => (
          <div key={idx} className={activeTab === idx ? 'block' : 'hidden'}>
            <ContentBlockFactory
              item={item}
              categoryId={categoryId}
              record={rawRecord}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

const SiteItemDetail = () => {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [state, setState] = useState<PageState>({
    mainTemplateRows: [],
    leftTemplateRows: [],
    rightTemplateRows: [],
    additionalSectionRows: [],
    record: null,
    rawRecord: null,
    blockCategoryId: null,
    templateType: 'full-width',
    loading: true,
    error: null,
    retryCount: 0,
  });

  // Set document title whenever the record changes
  useEffect(() => {
    if (state.record?.title) {
      document.title = state.record.title.replace(/<[^>]*>/g, "").trim();
    }
  }, [state.record]);

  useEffect(() => {
    const pathParts = pathname.split('/').filter(Boolean);
    const systemShortUrl = pathParts[0];   // e.g. "np-abc123"
    const referralUrl = pathParts[1];      // e.g. "ci-xyz789" (optional)
    const tplShortUrl = pathParts[2];      // e.g. "ci-xyz789" (optional)
    const templateId = searchParams.get('tpl');

    if (systemShortUrl) {
      loadPageData(systemShortUrl, referralUrl ?? null, templateId, tplShortUrl);
    }
  }, [pathname, searchParams]);

  // ─── Data loading ───────────────────────────────────────────────────────────

  const loadPageData = async (
    systemShortUrl: string,
    referralUrl: string | null,
    templateId: string | null = null,
    tplShortUrl: string | null = null
  ) => {
    try {
      setState((prev) => ({ ...prev, loading: true, error: null }));

      const prefix = systemShortUrl.split('-')[0];

      // If a ?tpl= override is present, fetch just that template (admin preview)
      const apiUrl = templateId
        ? `/api/templates?id=${templateId}&template_short_url=${encodeURIComponent(tplShortUrl)}`
        : `/api/templates?system_short_url=${encodeURIComponent(systemShortUrl)}&template_short_url=${encodeURIComponent(tplShortUrl)}`;

      const response = await fetch(apiUrl);
      if (!response.ok) throw new Error(`Failed to load page data (${response.status})`);

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Failed to load template');
      
      const rawRecord = data.record ?? data.category ?? null;
      
      console.log(data);

      if (!rawRecord && !templateId) throw new Error('Record not found');

      const record: PageRecord | null = rawRecord
        ? normaliseRecord(prefix, rawRecord)
        : null;

      // ── Referral category ───────────────────────────────────────────────────
      // The referral ci- URL serves two purposes:
      //   1. Always: provides blockCategoryId to filter content in blocks
      //   2. Only when record has NO template: provides the fallback template
      let blockCategoryId: number | null = null;
      let resolvedTemplate = data.template ?? null;

      if (referralUrl && referralUrl.startsWith('ci-')) {
        const refResponse = await fetch(
          `/api/templates?system_short_url=${encodeURIComponent(referralUrl)}&template_short_url=${encodeURIComponent(tplShortUrl)}`
        );
        if (refResponse.ok) {
          const refData = await refResponse.json();
          const refRecord = refData.record ?? refData.category ?? null;

          // Always extract blockCategoryId from referral category
          if (refData.success && refRecord?.id) {
            blockCategoryId = refRecord.id;
          }

          // Only use referral template when the record has no template of its own
          if (!resolvedTemplate && refData.success && refData.template) {
            resolvedTemplate = refData.template;
          }
        }
      }

      if (!resolvedTemplate) throw new Error('No template assigned to this page');

      // Fallback: use record's own cat_id if no referral was present
      if (!blockCategoryId && record?.cat_id) {
        blockCategoryId = record.cat_id;
      }

      // ── Process layout rows ─────────────────────────────────────────────────
      let mainTemplateRows: LayoutRow[] = resolvedTemplate.parsed_layout?.layout ?? [];

      if (blockCategoryId) {
        mainTemplateRows = processDynamicCategory(mainTemplateRows, blockCategoryId);
      }

      const templateType = resolvedTemplate.type || 'full-width';
      let leftTemplateRows: LayoutRow[] = [];
      let rightTemplateRows: LayoutRow[] = [];

      if (
        (templateType === 'left-sidebar' || templateType === 'both-sidebar') &&
        resolvedTemplate.lefttemplate
      ) {
        leftTemplateRows = await loadSidebarTemplate(resolvedTemplate.lefttemplate, blockCategoryId);
      }

      if (
        (templateType === 'right-sidebar' || templateType === 'both-sidebar') &&
        resolvedTemplate.righttemplate
      ) {
        rightTemplateRows = await loadSidebarTemplate(resolvedTemplate.righttemplate, blockCategoryId);
      }

      // ── Detail page additional section (news / video / profile) ─────────────
      // Chosen server-side: setting on? → item category → parent → fallback
      // category. Empty array = nothing to show. Never blocks the page.
      let additionalSectionRows = await loadAdditionalSection(systemShortUrl);
      if (blockCategoryId && additionalSectionRows.length) {
        additionalSectionRows = processDynamicCategory(additionalSectionRows, blockCategoryId);
      }

      setState({
        mainTemplateRows,
        leftTemplateRows,
        rightTemplateRows,
        additionalSectionRows,
        record,
        rawRecord: rawRecord ?? null,
        blockCategoryId,
        templateType: templateType as PageState['templateType'],
        loading: false,
        error: null,
        retryCount: 0,
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to load page content';
      console.error('Error loading page data:', error);
      setState((prev) => ({
        ...prev,
        loading: false,
        error: message,
        retryCount: prev.retryCount + 1,
      }));
    }
  };

  // ─── Sidebar loader ─────────────────────────────────────────────────────────

  const loadSidebarTemplate = async (
    templateId: string,
    blockCategoryId: number | null
  ): Promise<LayoutRow[]> => {
    try {
      const response = await fetch(`/api/templates?id=${templateId}`);
      if (!response.ok) return [];
      const data = await response.json();
      if (!data.success || !data.template?.parsed_layout?.layout) return [];

      return blockCategoryId
        ? processDynamicCategory(data.template.parsed_layout.layout, blockCategoryId)
        : data.template.parsed_layout.layout;
    } catch (error) {
      console.error('Error loading sidebar template:', error);
      return [];
    }
  };

  // ─── Additional section loader ──────────────────────────────────────────────

  const loadAdditionalSection = async (systemShortUrl: string): Promise<LayoutRow[]> => {
    const prefix = systemShortUrl.split('-')[0];
    if (!['np', 'vp', 'pd', 'nr'].includes(prefix)) return [];
    try {
      const response = await fetch(
        `/api/detail-section/auto?system_short_url=${encodeURIComponent(systemShortUrl)}`
      );
      if (!response.ok) return [];
      const data = await response.json();
      const rows = data?.section?.parsed_layout?.layout;
      return data?.success && Array.isArray(rows) ? rows : [];
    } catch (error) {
      console.error('Error loading detail page additional section:', error);
      return [];
    }
  };

  // ─── Block config injection ─────────────────────────────────────────────────

  /**
   * Walk every block config in the layout and replace 'dynamic' category
   * references with the resolved blockCategoryId.
   * Only runs when blockCategoryId is available (ci/ga pages, or pages with referral URL).
   */
  const processDynamicCategory = (
    rows: LayoutRow[],
    categoryId: number
  ): LayoutRow[] =>
    rows.map((row) => ({
      ...row,
      columns: row.columns.map((column) => ({
        ...column,
        items: column.items.map((item) => {
          const config = { ...item.config };
          if (typeof config.search_profile === 'string' && config.search_profile?.includes('category=dynamic')) {
            config.main_category = categoryId.toString();
            config.search_profile = config.search_profile.replace(
              'category=dynamic',
              `category=${categoryId}`
            );
          }
          if (config.category === 'dynamic') {
            config.category = categoryId.toString();
          }
          if (!config.main_category) {
            config.main_category = categoryId.toString();
          }
          return { ...item, config };
        }),
      })),
    }));

  // ─── Rendering helpers ──────────────────────────────────────────────────────

  const renderTemplate = (rows: LayoutRow[]) => {
    if (!rows || rows.length === 0) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">No content available</p>
        </div>
      );
    }

    return rows.map((row, rowIndex) => {
      return (
        <div
          key={row.rowId || `row-${rowIndex}`}
          className="grid grid-cols-12 gap-6 mb-8"
        >
          {row.columns.map((column, columnIndex) => {
            // Use explicit width from API (e.g. "9", "3") if present,
            // otherwise fall back to equal-split based on column count.
            const colSpanClass = column.width
              ? getColSpanFromWidth(column.width)
              : getDynamicColSpan(row.columns.length);
            return (
            <div
              key={column.columnId || `col-${columnIndex}`}
              className={colSpanClass}
            >
              {column.display_mode === 'tab' ? (
                <ColumnTabView
                  column={column}
                  categoryId={state.blockCategoryId?.toString()}
                  rawRecord={state.rawRecord}
                />
              ) : (
                column.items.map((item: ContentItem, itemIndex: number) => (
                  <div key={`item-${itemIndex}`} className="mb-8 last:mb-0">
                    <ContentBlockFactory
                      item={item}
                      categoryId={state.blockCategoryId?.toString()}
                      record={state.rawRecord}
                    />
                  </div>
                ))
              )}
            </div>
            );
          })}
        </div>
      );
    });
  };

  /**
   * Maps an explicit API column width (1–12, Bootstrap-style grid)
   * to a Tailwind col-span class.
   * Falls back to col-span-12 for unknown values.
   */
  const getColSpanFromWidth = (width: string | number | undefined): string => {
    // Mobile-first: every admin-configured column is full width until the
    // `lg` breakpoint, then takes its configured share of the 12-col grid.
    // Without the `col-span-12` base, a narrow admin-set column (e.g. "3")
    // would render as a cramped sliver on phone screens instead of stacking.
    const colSpanMap: Record<number, string> = {
      1:  'col-span-12 lg:col-span-1',
      2:  'col-span-12 lg:col-span-2',
      3:  'col-span-12 lg:col-span-3',
      4:  'col-span-12 lg:col-span-4',
      5:  'col-span-12 lg:col-span-5',
      6:  'col-span-12 lg:col-span-6',
      7:  'col-span-12 lg:col-span-7',
      8:  'col-span-12 lg:col-span-8',
      9:  'col-span-12 lg:col-span-9',
      10: 'col-span-12 lg:col-span-10',
      11: 'col-span-12 lg:col-span-11',
      12: 'col-span-12',
    };
    const numeric = Number(width);
    return colSpanMap[numeric] || 'col-span-12';
  };

  /** @deprecated — kept only as fallback when column has no width field */
  const getDynamicColSpan = (columnsLength: number): string => {
    const colSpanMap: Record<number, string> = {
      1: 'col-span-12',
      2: 'col-span-12 sm:col-span-6',
      3: 'col-span-12 sm:col-span-6 lg:col-span-4',
      4: 'col-span-12 sm:col-span-6 lg:col-span-3',
      6: 'col-span-6 sm:col-span-4 lg:col-span-2',
    };
    return colSpanMap[columnsLength] || 'col-span-12';
  };

  const getLayoutClasses = () => ({
    container: 'flex flex-col lg:flex-row gap-6',
    leftSidebar: 'w-full lg:w-1/4 order-2 lg:order-1',
    main: `flex-1 order-1 lg:order-2 ${
      state.templateType === 'full-width'
        ? 'w-full'
        : state.templateType === 'both-sidebar'
        ? 'lg:w-1/2'
        : 'lg:w-3/4'
    }`,
    rightSidebar: 'w-full lg:w-1/4 order-3',
  });

  const handleRetry = () => {
    const pathParts = pathname.split('/').filter(Boolean);
    const systemShortUrl = pathParts[0];
    const referralUrl = pathParts[1] ?? null;
    const templateId = searchParams.get('tpl');
    if (systemShortUrl) loadPageData(systemShortUrl, referralUrl, templateId);
  };

  // ─── Render states ──────────────────────────────────────────────────────────

  if (state.loading) {
    return (
      <div className="container mx-auto py-0">
        <div className="mb-8">
          <div className="h-10 w-64 bg-gray-200 rounded-lg animate-pulse mb-4" />
          <div className="h-4 w-96 bg-gray-200 rounded animate-pulse" />
        </div>
        <div className="flex gap-6">
          <div className="flex-1">
            <ContentBlockSkeleton type="news" columns={3} />
          </div>
        </div>
      </div>
    );
  }
  
  if (state.error) {
    return (
      <div className="container mx-auto py-0">
        <div className="max-w-2xl mx-auto bg-red-50 border border-red-200 rounded-lg p-8 text-center">
          <svg
            className="w-16 h-16 text-red-500 mx-auto mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <h2 className="text-2xl font-bold text-red-800 mb-2">
            {state.retryCount > 2 ? 'Unable to Load Page' : 'Page Not Found'}
          </h2>
          <p className="text-red-600 mb-6">{state.error}</p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={handleRetry}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              Try Again
            </button>
            <a
              href="/"
              className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
            >
              Go Home
            </a>
          </div>
          {state.retryCount > 0 && (
            <p className="text-sm text-gray-500 mt-4">
              Retry attempts: {state.retryCount}
            </p>
          )}
        </div>
      </div>
    );
  }

  const layoutClasses = getLayoutClasses();
  const hasLeftSidebar =
    state.leftTemplateRows.length > 0 &&
    (state.templateType === 'left-sidebar' || state.templateType === 'both-sidebar');
  const hasRightSidebar =
    state.rightTemplateRows.length > 0 &&
    (state.templateType === 'right-sidebar' || state.templateType === 'both-sidebar');

  // ─── Main render ────────────────────────────────────────────────────────────

  return (
    <PageErrorBoundary>
      <div className="container mx-auto py-0">

        {/* Record header — title and optional description */}
        

        <div className={layoutClasses.container}>
          {hasLeftSidebar && (
            <aside className={layoutClasses.leftSidebar}>
              <div className="lg:sticky lg:top-4 space-y-6">
                {renderTemplate(state.leftTemplateRows)}
              </div>
            </aside>
          )}

          <main className={layoutClasses.main}>
            {state.mainTemplateRows.length > 0 ? (
              renderTemplate(state.mainTemplateRows)
            ) : (
              <div className="text-center py-12 bg-gray-50 rounded-lg">
                <svg
                  className="w-16 h-16 text-gray-400 mx-auto mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <p className="text-gray-600">No content available for this page.</p>
              </div>
            )}

            {/* Detail page additional section — after the template */}
            {state.additionalSectionRows.length > 0 && (
              <section className="detail-additional-section">
                {renderTemplate(state.additionalSectionRows)}
              </section>
            )}
          </main>

          {hasRightSidebar && (
            <aside className={layoutClasses.rightSidebar}>
              <div className="lg:sticky lg:top-4 space-y-6">
                {renderTemplate(state.rightTemplateRows)}
              </div>
            </aside>
          )}
        </div>
      </div>
    </PageErrorBoundary>
  );
};

export default SiteItemDetail;